import {Container, Graphics, Sprite, Texture} from "pixi.js";
import {DRect} from "src/app/binst/primitives/drawable-rect";
import {PixiObject} from "../primitives/pixi-object";
import {BlueprintContext} from "../../../world/cyanprints/i-blueprint-context";
import {Rect, RectConstructor} from "../../../primitives/rect";
import {Surface} from "../../../surface/primitives/surface";
import {Subject} from "rxjs";


type ImageDrawableConfig = ConstructorParameters<typeof Sprite>[0] & {
    base64Source?: string;
    url?: string;
    id?: string;
    fileId?: string;
    fitInSquareSize?: number;
    blueprintContext: BlueprintContext;
    borderRadius?: number;
    preferLandscapeRatio?: boolean;
    centerInSprite?: boolean;
    debug?: boolean;
}

export class ImageDrawable extends PixiObject {
    public imageSprite: Sprite;
    public placeholder: Graphics;
    public mask: Graphics;
    override config: ImageDrawableConfig
    blueprintContext: BlueprintContext;
    imageLoaded = false
    debug = false
    widthHeightSubject: Subject<{ width: number, height: number }> = new Subject();

    constructor(config: ImageDrawableConfig) {
        super(config);
        this.config = config;
        this.blueprintContext = config.blueprintContext;
        this.createOrUpdate({})
    }

    async createOrUpdate(config: {
        base64Source?: string;
        url?: string;
        id?: string;
        fileId?: string;
    }) {
        this.config = {...this.config, ...config}
        this.debug = this.config.debug
        if (this.imageSprite) {
            this.sprite.removeChild(this.imageSprite);
            this.imageSprite.destroy();
        }
        if (this.placeholder) {
            this.sprite.removeChild(this.placeholder);
            this.placeholder.destroy();
        }
        this.addPlaceholder(this.config.fitInSquareSize ?? 50, this.config.fitInSquareSize ?? 50);
        await this.build();
    }

    async build() {
        if (this.config.url) {
            this.fromUrl(this.config.url);
            return
        }
        if (this.config.base64Source) {
            let base64Source = this.config.base64Source;
            ImageDrawable.TextureFromBase64(base64Source)
                .then(texture => {
                    this.base64Loaded(texture);
                })
            return
        }
        if (this.config.fileId) {
            setTimeout(async () => {
                const file = await this.blueprintContext
                    .networkServices
                    .fileService.getFile(this.config.fileId)
                const base64Source = file.fileData
                ImageDrawable.TextureFromBase64(base64Source)
                    .then(texture => {
                        this.base64Loaded(texture);
                    })
            }, Math.random() * 100); // Stagger loading of files to avoid overloading the server
            return
        }
    }
    fromUrl(url: string) {
        this.imageSprite = Sprite.from(this.config.url);
        this.sprite.addChild(this.imageSprite);
        this.placeholder.destroy()
        this.imageLoaded = true
        this.widthHeightSubject.next({width: this.config.width, height: this.config.height})
    }

    center() {
        this.config.centerInSprite = true;
    }


    base64Loaded(texture) {
        let width = texture.width;
        let height = texture.height;
        let x = 0
        let y = 0
        if (this.config.fitInSquareSize) {
            const dimens = this.fitInSquare(texture.width, texture.height, this.config.fitInSquareSize)
            width = dimens.width;
            height = dimens.height;
        }
        this.imageSprite = new Sprite({
            x: this.config.centerInSprite ? x : 0,
            y: this.config.centerInSprite ? y : 0,
            width: width,
            height: height,
            texture: texture
        })
        this.mask = this.makeBorderRadiusMask(
            width,
            height,
            this.config.borderRadius ?? 0
        )
        this.imageSprite.mask = this.mask
        this.sprite.addChild(this.imageSprite);
        this.sprite.addChild(this.mask);
        this.placeholder.destroy()
        this.imageLoaded = true
        // this.updateTouchBounds()
        this.widthHeightSubject.next({width: texture.width, height: texture.height})
        if (this._surface) this._surface.markForRender()
    }

    // static async CreateWithBase64Image(config: {
    //       b64Source: string;
    //     }) {
    //   const texture = await this.TextureFromBase64(config.b64Source)
    //   return new ImageDrawable({...config, texture})
    // }

    static async TextureFromBase64(base64: string): Promise<Texture> {
        const res = await fetch(base64);         // fetch decodes base64
        const blob = await res.blob();           // turn into Blob
        const bitmap = await createImageBitmap(blob);
        return Texture.from(bitmap);
    }


    override setRect(config: RectConstructor) {
        const r = new Rect(config)
        this.imageSprite.x =  r.x;
        this.imageSprite.y = r.y;
        this.imageSprite.width = r.width
        this.imageSprite.height = r.height
        this.updateTouchBounds()
    }

    override setupDefaultInteractions() {
        // this.touchContainer.onPressed(async (pt) => {
        //     this.imageSprite.alpha = 0.5;
        //     this.requestRender()
        //     return true; // Stop propagation
        // });
        // this.touchContainer.onReleased(async (pt) => {
        //     this.imageSprite.alpha = 1;
        //     this.requestRender()
        //     return true; // Stop propagation
        // });
    }

    makeBorderRadiusMask(width, height, radius: number) {
        const graphics = new Graphics({});
        const rect = new DRect({
            left: 0,
            top: 0,
            right: width,
            bottom: height,
            roundness: radius,
            colour: 0xFFFFFF,
        });
        rect.render(graphics);
        return graphics;
    }

    addPlaceholder(width, height) {
        this.placeholder = new Graphics({});
        const rect = new DRect({
            left: 0,
            top: 0,
            right: width,
            bottom: height,
            roundness: this.config.borderRadius ?? 0,
            colour: "rgba(58,52,49,0.53)",
        });
        rect.render(this.placeholder);
        this.sprite.addChild(this.placeholder);
    }

    addDebugRect(width, height) {
        const graphics = new Graphics({});
        const rect = new DRect({
            left: 0,
            top: 0,
            right: width,
            bottom: height,
            roundness: 5,
            colour: "rgba(58,52,49,0.53)",
        });
        rect.render(graphics);
        this.sprite.addChild(graphics);
    }


    override async onAddedToSurface(surface: Surface) {
        super.onAddedToSurface(surface);
        surface.markForRender()
    }

    getWidthHeightSubject() {
        return this.widthHeightSubject
    }


    fitInSquare(width, height, maxSquareSize) {
        const MAX_SIZE = maxSquareSize;
        let ratio = 0
        if (width > height) {
            let diff = 0
            // if (this.config.preferLandscapeRatio ?? false) {
            //     diff = (1-(height / width)) * 0.3
            // }
            ratio = (MAX_SIZE / width) + diff
        }
        if (height >= width) {
            let diff = 0
            if (this.config.preferLandscapeRatio ?? false) {
                diff = (1 - (width / height)) * 0.1
            }
            ratio = (MAX_SIZE / height) - diff
        }
        return {
            width: Math.round(width * ratio),
            height: Math.round(height * ratio)
        };
    }
}